import React, { useState } from 'react';

import { FormDialog } from '@components/admin/FormDialog';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { SemiAutoCompleteOption } from '@components/TextBox';
import { HandleCNZipcodeChange } from '@components/utilities/Contact/ContactForm';
import { getCityLabelByName, warehouseCityNames } from '@data/cities';
import { warehouseStrings } from '@data/warehouse';
import { warehouseForm, WarehouseForm } from '@models/Warehouse';
import { makeMuiStyles, useIsMobile } from '@styles';
import { createValidate } from '@utils/forms';
import { createStrings } from '@utils/local';
import { Formik, Form, useFormikContext } from 'formik';

const strings = createStrings({
  ja: {
    addWarehouseTitle: '倉庫を追加する',
    editWarehouseTitle: '倉庫を編集する',
  },
});

const useStyles = makeMuiStyles((_theme, _common) => ({
  body: {
    overflow: 'auto',
    maxWidth: '100%',
    width: '60rem',
  },
}));

const AddressFields: React.FC = () => {
  const [choices, setChoices] = useState<SemiAutoCompleteOption[]>([]);
  const isMobile = useIsMobile();
  const formik = useFormikContext();

  return (
    <>
      <HandleCNZipcodeChange
        zipCodeFieldName={'zipcode'}
        addressFieldName={'address1'}
        addressCNFieldName="address1_cn"
        onZipCodeSemiAutoCompleteOptions={(items: SemiAutoCompleteOption[]) => {
          setChoices(items);
        }}
      />
      <ManagementTextInput
        name={'zipcode'}
        label={warehouseStrings.zipcode}
        labelDestructive={isMobile}
        withSemiAutoComplete
        semiAutoCompleteOptions={choices}
        onSemiAutoCompleteOptionSelect={(option: SemiAutoCompleteOption) => {
          const { zipcode, address } = option.value;
          formik.setFieldValue('zip_code', zipcode, true);
          formik.setFieldValue('address1', address, true);
        }}
        required
      />
      <ManagementTextInput
        name={'address1'}
        label={warehouseStrings.address1}
        labelDestructive={isMobile}
        required
      />
      <ManagementTextInput
        name={'address2'}
        label={warehouseStrings.address2}
        labelDestructive={isMobile}
        required
      />
    </>
  );
};

interface WarehouseAddEditDialogProps {
  open: boolean;
  onClose: () => void;
  action: 'create' | 'edit';
  onSubmit: (values: WarehouseForm) => Promise<void>;
  initialValues: WarehouseForm;
}

export const WarehouseAddEditDialog: React.FC<WarehouseAddEditDialogProps> = (props) => {
  const { open, onClose, onSubmit, action, initialValues } = props;

  const classes = useStyles();
  const isMobile = useIsMobile();

  return (
    <FormDialog
      dialogProps={{ maxWidth: false }}
      title={action === 'create' ? strings.addWarehouseTitle : strings.editWarehouseTitle}
      alignTitle="center"
      open={open}
      onClose={onClose}
      classes={{ body: classes.body }}
    >
      <Formik<WarehouseForm>
        initialValues={initialValues}
        onSubmit={async (values) => await onSubmit(values)}
        validate={createValidate(warehouseForm)}
      >
        {() => (
          <Form>
            <SubmittingOverlay />
            <ManagementTextInput
              name={'name'}
              label={warehouseStrings.name}
              labelDestructive={isMobile}
              required
            />
            <ManagementTextInput
              name={'name_en'}
              label={warehouseStrings.englishName}
              labelDestructive={isMobile}
              required
            />
            <ManagementTextInput
              name={'city'}
              choose
              label={warehouseStrings.city}
              labelDestructive={isMobile}
              options={warehouseCityNames.map((name) => ({
                label: getCityLabelByName(name),
                value: name,
              }))}
              required
            />
            <AddressFields />
            <ManagementTextInput
              name={'principal_name'}
              label={warehouseStrings.principalName}
              labelDestructive={isMobile}
              required
            />
            <ManagementTextInput
              name={'phone'}
              label={warehouseStrings.phone}
              labelDestructive={isMobile}
              required
            />
            <ManagementTextInput
              name={'memo'}
              label={warehouseStrings.memo}
              labelDestructive={isMobile}
              required
            />
          </Form>
        )}
      </Formik>
    </FormDialog>
  );
};
